import React, { useRef, useState } from "react";
import { Table } from "@components/Table";
import { IconButton, Dialog, Select, Button } from "@narmi/design_system";
import { useUserConfiguration } from "@hooks/useUserConfiguration";
import ReportsPrefilteringModal, { type ReportType } from "./ReportsPrefilteringModal";

const ReportsPageReportsTable = ({
  legacy_reports,
  availableBillingMonths,
  displayBillingReports,
  hasFedNowReportAccess,
}: {
  legacy_reports: { [key: string]: { title: string; description: string; download_url: string } };
  availableBillingMonths: { name: string; uuid: string }[];
  displayBillingReports: boolean;
  hasFedNowReportAccess: boolean;
}) => {
  const { isFeatureEnabled } = useUserConfiguration();

  const downloadBillingReport = useRef<HTMLFormElement>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(availableBillingMonths[0]);

  const [isPrefilteringModalOpen, setIsPrefilteringModalOpen] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState<ReportType | "">("");
  const [selectedReportTitle, setSelectedReportTitle] = useState("");

  const handleSubmit = () => {
    if (downloadBillingReport.current) {
      downloadBillingReport.current.submit();
    }
  };

  // Reports that support prefiltering (date filtering, deduplication, column selection)
  const PREFILTER_ENABLED_REPORTS = [
    "consumer_account_applicants",
    "business_account_applicants",
    "consumer_staffer_led_account_applicants",
    "business_staffer_led_account_applicants",
    "lending_applications",
  ];

  const handleReportDownload = (reportKey: string, reportTitle: string, downloadUrl: string) => {
    if (isFeatureEnabled("reports_prefiltering") && PREFILTER_ENABLED_REPORTS.includes(reportKey)) {
      // Use prefiltering modal for applicant reports only
      setSelectedReportType(reportKey as ReportType);
      setSelectedReportTitle(reportTitle);
      setIsPrefilteringModalOpen(true);
    } else if (reportKey === "billing") {
      // Keep existing billing dialog
      setIsDialogOpen(true);
    } else {
      // Fallback to direct download
      window.location.href = downloadUrl;
    }
  };

  const allReports = [];

  Object.keys(legacy_reports).forEach((key) => {
    allReports.push({
      type: "legacy",
      key,
      title: legacy_reports[key].title,
      description: legacy_reports[key].description,
      download_url: legacy_reports[key].download_url,
    });
  });

  if (displayBillingReports && availableBillingMonths.length > 0) {
    allReports.push({
      type: "billing",
      key: "billing",
      title: "Billing",
      description: "A list of billing statistics visible only to Narmi.",
    });
  }

  if (
    (isFeatureEnabled("fednow_receive") || isFeatureEnabled("fednow_send")) &&
    hasFedNowReportAccess
  ) {
    allReports.push({
      type: "fednow",
      key: "fednow-nonterminal",
      title: "FedNow non-terminal transactions report (live)",
      description:
        "All non-terminal FedNow transactions as of right now, fetched on demand rather than from last night's build.",
      download_url: "/v1/fednow/report_download?report_type=nonterminal",
    });
  }

  allReports.sort((a, b) => a.title.localeCompare(b.title));

  return (
    <>
      <Table>
        <Table.ColGroup>
          <Table.Col width="90%" />
          <Table.Col width="10%" />
        </Table.ColGroup>
        <Table.Header>
          <Table.HeaderCell>Name</Table.HeaderCell>
          <Table.HeaderCell aria-label="Download" />
        </Table.Header>
        <Table.Body>
          {allReports.map((report) => (
            <Table.Row key={report.key}>
              <Table.Cell>
                <div className="fontWeight--bold">{report.title}</div>
                <div>{report.description}</div>
              </Table.Cell>
              <Table.Cell>
                <IconButton
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    handleReportDownload(report.key, report.title, report.download_url || "");
                  }}
                  name="download"
                  textSize="l"
                >
                  Download
                </IconButton>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {displayBillingReports && availableBillingMonths.length > 0 && (
        <form action={`/billing/${selectedMonth.uuid}`} method="GET" ref={downloadBillingReport}>
          <input type="hidden" name="uuid" value={selectedMonth.uuid} />
          <Dialog
            isOpen={isDialogOpen}
            footer={
              <div style={{ textAlign: "right" }} className="margin--top--l">
                <Button label="Cancel" kind="negative" onClick={() => setIsDialogOpen(false)} />
                <span className="margin--left--s">
                  <Button label="Download" onClick={handleSubmit} />
                </span>
              </div>
            }
            title="Download billing report"
            onUserDismiss={() => {
              setIsDialogOpen(false);
            }}
          >
            <div className="padding--top--m">
              <Select
                label="Month"
                onChange={(uuid: string) => {
                  const month = availableBillingMonths.find((report) => report.uuid === uuid);
                  if (month) {
                    setSelectedMonth(month);
                  }
                }}
                defaultValue={selectedMonth.uuid}
              >
                {availableBillingMonths.map((report) => (
                  <Select.Item value={report.uuid} key={report.uuid}>
                    {report.name}
                  </Select.Item>
                ))}
              </Select>
            </div>
          </Dialog>
        </form>
      )}
      <ReportsPrefilteringModal
        isOpen={isPrefilteringModalOpen}
        onClose={() => setIsPrefilteringModalOpen(false)}
        reportType={selectedReportType as ReportType}
        reportTitle={selectedReportTitle}
      />
    </>
  );
};

export default ReportsPageReportsTable;
